class PushNotifications {
    constructor() {
        this.vapidPublicKey = document.querySelector('meta[name="vapid-public-key"]')?.content;
    }

    isSupported() {
        return 'serviceWorker' in navigator && 'PushManager' in window && !!this.vapidPublicKey;
    }

    async getPermissionState() {
        if (!this.isSupported()) return 'unsupported';
        return Notification.permission; // 'default', 'granted', 'denied'
    }

    async requestPermission() {
        if (!this.isSupported()) return false;
        const permission = await Notification.requestPermission();
        return permission === 'granted';
    }

    async subscribe() {
        if (!this.isSupported()) {
            throw new Error('Push notifications are not supported in this browser or not configured on the server.');
        }

        const permission = await this.requestPermission();
        if (!permission) return null;

        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: this._urlBase64ToUint8Array(this.vapidPublicKey)
        });

        // Send subscription to server
        const response = await fetch('/push-subscriptions', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
            },
            body: JSON.stringify(subscription.toJSON())
        });

        return response.ok ? subscription : null;
    }

    async unsubscribe() {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();

        if (!subscription) return true;

        // Remove from server
        await fetch('/push-subscriptions', {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
            },
            body: JSON.stringify({ endpoint: subscription.endpoint })
        });

        await subscription.unsubscribe();
        return true;
    }

    async getCurrentSubscription() {
        if (!this.isSupported()) return null;

        const registration = await navigator.serviceWorker.ready;
        return registration.pushManager.getSubscription();
    }

    async ensureSubscription() {
        if (!this.isSupported()) return null;
        if (Notification.permission !== 'granted') return null;

        const registration = await navigator.serviceWorker.ready;
        let subscription = await registration.pushManager.getSubscription();

        // Always unsubscribe and resubscribe to get a fresh endpoint.
        // FCM endpoints can expire silently, so refreshing on settings
        // page visit ensures the server always has a valid endpoint.
        var oldEndpoint = subscription ? subscription.endpoint : null;
        if (subscription) {
            await subscription.unsubscribe();
        }

        subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: this._urlBase64ToUint8Array(this.vapidPublicKey)
        });

        // Send subscription to server, including old endpoint for cleanup
        var payload = subscription.toJSON();
        if (oldEndpoint && oldEndpoint !== payload.endpoint) {
            payload.old_endpoint = oldEndpoint;
        }

        const response = await fetch('/push-subscriptions', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
            },
            body: JSON.stringify(payload)
        });

        return response.ok ? subscription : null;
    }

    _urlBase64ToUint8Array(base64String) {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }
}

window.PushNotifications = new PushNotifications();
